import { CatalogPriceSource } from '@codezo/graphql';
import { useMemo } from 'react';
import type { CatalogPriceRung, CatalogSuggestion } from './catalog-types';
import type { CatalogOption } from './CatalogSourcePicker';
import { panelUnitMoney } from './panel-format';
import { saleFromMarkup, type TierEntryMode } from './tier-pricing';

/** What a supplier or the catalogue actually quoted, before any conversion. */
export type QuotedPrice = { baseUnitPrice: number; currency: string };

/**
 * A rung's cost in the currency the form saves in, or null when it has none.
 *
 * The backend restates every suggestion in the form's currency (`converted`),
 * so a price kept in EUR is as usable as one kept in DKK. Only a pair with no
 * exchange rate leaves `converted` null, and then a price already in the
 * form's currency is the only usable one. `quoted` is set when a rate was
 * applied, so the form can say what was really quoted.
 */
export function inFormCurrency(
	rung: Pick<CatalogPriceRung, 'baseUnitPrice' | 'currency' | 'converted'>,
	formCurrency: string
): { baseUnitPrice: number; quoted: QuotedPrice | null } | null {
	if (rung.converted && rung.converted.currency === formCurrency) {
		return {
			baseUnitPrice: rung.converted.baseUnitPrice,
			quoted:
				rung.currency === formCurrency
					? null
					: { baseUnitPrice: rung.baseUnitPrice, currency: rung.currency },
		};
	}
	if (rung.currency === formCurrency) return { baseUnitPrice: rung.baseUnitPrice, quoted: null };
	return null;
}

/** The line shown beside a converted figure. */
export const quotedNote = (quoted: QuotedPrice) =>
	`Converted from ${panelUnitMoney(quoted.baseUnitPrice, quoted.currency)} at today’s rate`;

/** The figures a catalogue rung writes into a tier. Shared by both pricing forms. */
export type CatalogueFill = {
	cost: string;
	markup: string;
	salePrice: string;
	entryMode: TierEntryMode;
	sourceQuantity: number;
	sourceVetterEntityId: string | null;
	minDays: string;
	maxDays: string;
	quoted: QuotedPrice | null;
};

/**
 * Fills a tier from ONE rung of the ladder, in the form's currency. Null when
 * the rung cannot be restated in it.
 *
 * The catalogue quotes a cost and a markup, so that is the way round this
 * arrives; the sale price follows from it. Markup and production days are
 * currency-free and carried as they are. Days the rung is silent about keep
 * `prev`: a catalogue row without them is not an instruction to forget the ones
 * the admin typed.
 */
export function catalogueFill(
	rung: CatalogPriceRung,
	formCurrency: string,
	prev?: { minDays: string; maxDays: string }
): CatalogueFill | null {
	const price = inFormCurrency(rung, formCurrency);
	if (!price) return null;
	const cost = String(price.baseUnitPrice);
	const markup = rung.markupPercentage != null ? String(rung.markupPercentage) : '';
	return {
		cost,
		markup,
		salePrice: markup ? saleFromMarkup(cost, markup) : '',
		entryMode: 'markup',
		sourceQuantity: rung.quantity,
		sourceVetterEntityId: rung.vetterEntityId ?? null,
		minDays:
			rung.minProductionDays != null ? String(rung.minProductionDays) : (prev?.minDays ?? ''),
		maxDays:
			rung.maxProductionDays != null ? String(rung.maxProductionDays) : (prev?.maxDays ?? ''),
		quoted: price.quoted,
	};
}

/** The break a suggestion applied, as a rung. */
export const appliedRung = (sug: CatalogSuggestion): CatalogPriceRung => ({
	quantity: sug.matchedQuantity,
	vetterEntityId: sug.vetterEntity?.id ?? null,
	baseUnitPrice: sug.baseUnitPrice,
	currency: sug.currency,
	markupPercentage: sug.markupPercentage,
	minProductionDays: sug.minProductionDays,
	maxProductionDays: sug.maxProductionDays,
	converted: sug.converted,
});

/**
 * A suggestion answered by the supplier's own price request rather than the
 * catalogue. Not a catalogue break, so no catalogue row is "the one applied".
 */
export const isVetterAnswer = (sug: Pick<CatalogSuggestion, 'source'>) =>
	sug.source === CatalogPriceSource.Vetter;

/** A row of `getProductPrices`, as far as the picker needs it. */
type CatalogueRow = {
	quantity: number;
	baseUnitPrice: number;
	currency: string;
	markupPercentage?: number | null;
	minProductionDays?: number | null;
	maxProductionDays?: number | null;
	vetterEntityId?: string | null;
	vetterEntity?: { name: string } | null;
	/** The row in the form's currency, converted by the backend. */
	converted?: { currency: string; baseUnitPrice: number } | null;
};

/**
 * The catalogue's suggestions for one form, and the picker's list per tier.
 *
 * Every picker row carries its own `converted` figure, so any row is usable
 * whatever currency it is stored in — the assignment alone decides that.
 *
 * `exactFills` is the exact-match rule both forms apply on their own rows:
 * same volume AND source (this supplier's own row, or the no-supplier row when
 * none is assigned), with a markup, and restatable in the form's currency.
 */
export function useCatalogSuggestions<S extends CatalogSuggestion>({
	list,
	cataloguePrices,
	assignedVetterId,
	formCurrency,
}: {
	list: S[];
	cataloguePrices: CatalogueRow[];
	assignedVetterId: string | null;
	formCurrency: string;
}) {
	// Keyed by quantity id, never by quantity: a tier edited from 100 to 110 keeps
	// its id, and matching on the number silently reprices the wrong row.
	const suggestions = useMemo(
		() => new Map(list.map((s) => [s.orderQuoteItemQuantityId, s])),
		[list]
	);

	const optionsFor = (quantityId: string): CatalogOption[] => {
		const sug = suggestions.get(quantityId);
		const answeredBy = sug?.vetterEntity?.id ?? null;
		return cataloguePrices
			.map((row) => ({
				quantity: row.quantity,
				baseUnitPrice: row.baseUnitPrice,
				currency: row.currency,
				markupPercentage: row.markupPercentage,
				minProductionDays: row.minProductionDays,
				maxProductionDays: row.maxProductionDays,
				vetterEntityId: row.vetterEntityId ?? null,
				vetterName: row.vetterEntity?.name ?? null,
				converted: row.converted ?? null,
				// Usable when it belongs to nobody, or to the supplier assigned here.
				eligible: row.vetterEntityId == null || row.vetterEntityId === assignedVetterId,
				matched:
					!!sug &&
					!isVetterAnswer(sug) &&
					row.quantity === sug.matchedQuantity &&
					(row.vetterEntityId ?? null) === answeredBy,
				match: sug?.match,
			}))
			.sort(
				(a, b) =>
					Number(a.vetterEntityId != null) - Number(b.vetterEntityId != null) ||
					a.quantity - b.quantity
			);
	};

	// Memoised: the forms apply these from an effect, which must not re-run on
	// every render.
	const exactFills = useMemo(() => {
		const fills: Array<{ quantityId: string; fill: CatalogueFill }> = [];
		for (const sug of list) {
			if (sug.match !== 'EXACT') continue;
			// A row with no markup yields a cost and no price, which would trip the
			// unpriced-tier block over data nobody entered.
			if (sug.markupPercentage == null) continue;
			if ((sug.vetterEntity?.id ?? null) !== assignedVetterId) continue;
			const fill = catalogueFill(appliedRung(sug), formCurrency);
			if (fill) fills.push({ quantityId: sug.orderQuoteItemQuantityId, fill });
		}
		return fills;
	}, [list, assignedVetterId, formCurrency]);

	return { suggestions, optionsFor, exactFills };
}
